/**
 * DOM event binding, and the complete mouse and keyboard control surface
 * (U21 + U14).
 *
 * The split is deliberate: this file knows about the DOM, and `select.ts`,
 * `orders.ts`, `camera.ts`, `keys.ts`, and `placement.ts` know about the game.
 * That is what makes order resolution testable without a browser.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import { Selection } from './select.js'
import { Orders } from './orders.js'
import { CameraControl } from './camera.js'
import { ControlGroups } from './keys.js'
import { PlacementMode } from './placement.js'
import { Renderer } from '../render/index.js'
import { Entities, EF } from '../sim/entities.js'
import { defById, ROLE } from '../sim/data/entities.js'
import * as fix from '../core/fixed.js'

/** Sidebar tab shortcuts, matching Red Alert 2. */
/**
 * Sidebar tabs on the number row.
 *
 * Red Alert 2 puts these on Q/W/E/R. They are on the digits here because that
 * is what was asked for, and it costs only one thing: control-group recall
 * takes Shift, since the bare digits are no longer free.
 */
const TAB_KEYS: Record<string, number> = { Digit1: 0, Digit2: 1, Digit3: 2, Digit4: 3 }

export interface InputHooks {
  /** Q/W/E/R selected a sidebar tab. */
  onTab(tab: number): void
  /** The player asked to centre the camera on a world position. */
  onCenter(worldX: number, worldZ: number): void
  /** B — place the structure that finished most recently. */
  onQuickPlace(): void
  /** M — music on or off. */
  onToggleMusic(): void
  /** V — the CRT filter on or off. */
  /** F3 — the network diagnostics panel. */
  onToggleNetPanel(): void
  /** + — cycle the game speed. */
  onCycleSpeed(): void
}

export class InputController implements Subsystem {
  static id = 'input'
  static deps = ['selection', 'orders', 'camera', 'groups', 'placement', 'render', 'entities'] as const

  private ctx!: Ctx
  private selection!: Selection
  private orders!: Orders
  private camera!: CameraControl
  private groups!: ControlGroups
  private placement!: PlacementMode
  private renderer!: Renderer
  private entities!: Entities

  hooks: InputHooks | null = null
  localSlot = 0

  private readonly disposers: Array<() => void> = []
  private dragging = false
  private panning = false
  private touchStart: { x: number; y: number } | null = null
  private panX = 0
  private panY = 0
  private readonly boxEl: HTMLDivElement
  private pointerX = 0
  private pointerY = 0
  private lastCycledHarvester = 0

  constructor(private readonly root: HTMLElement) {
    this.boxEl = document.createElement('div')
    this.boxEl.style.cssText =
      'position:absolute;border:1px solid #cfe4ff;background:rgba(120,180,255,0.14);' +
      'pointer-events:none;display:none;z-index:5'
    this.root.appendChild(this.boxEl)
  }

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.selection = ctx.get<Selection>('selection')
    this.orders = ctx.get<Orders>('orders')
    this.camera = ctx.get<CameraControl>('camera')
    this.groups = ctx.get<ControlGroups>('groups')
    this.placement = ctx.get<PlacementMode>('placement')
    this.renderer = ctx.get<Renderer>('render')
    this.entities = ctx.get<Entities>('entities')

    const on = <K extends keyof WindowEventMap>(
      target: EventTarget,
      type: K,
      fn: (e: WindowEventMap[K]) => void,
    ) => {
      target.addEventListener(type, fn as EventListener)
      this.disposers.push(() => target.removeEventListener(type, fn as EventListener))
    }

    on(window, 'pointerdown', (e) => this.onPointerDown(e))
    on(window, 'pointermove', (e) => this.onPointerMove(e))
    on(window, 'pointerup', (e) => this.onPointerUp(e))
    on(window, 'pointercancel', () => {
      this.endPan()
      this.dragging = false
      this.boxEl.style.display = 'none'
    })
    on(window, 'keydown', (e) => this.onKeyDown(e))
    on(window, 'keyup', (e) => {
      this.camera.onKeyUp(e.code)
      if (e.code === 'KeyZ') this.waypointMode = false
    })
    on(window, 'blur', () => {
      this.camera.clearKeys()
      this.endPan()
      this.dragging = false
      this.boxEl.style.display = 'none'
      this.camera.edgeScrollEnabled = true
      // A key held when the window loses focus never sends its keyup, and a
      // stuck waypoint mode turns every later right-click into a route.
      this.waypointMode = false
    })
    on(window, 'contextmenu', (e) => e.preventDefault())
    on(window, 'pointerout', (e) => {
      if (!e.relatedTarget) this.camera.onPointerLeave()
    })
  }

  // --- pointer --------------------------------------------------------------

  private onPointerDown(e: PointerEvent): void {
    if (!this.camera.isInsideViewport(e.clientX, e.clientY)) return
    if (e.button === 1 || (e.pointerType === 'touch' && !this.placement.active)) {
      e.preventDefault()
      this.panX = e.clientX
      this.panY = e.clientY
      this.touchStart = e.pointerType === 'touch' ? { x: e.clientX, y: e.clientY } : null
      this.panning = e.button === 1
      this.camera.edgeScrollEnabled = false
      if (this.panning) this.root.style.cursor = 'grabbing'
      return
    }

    // A modal cursor swallows the click entirely: in placement mode a left
    // click places and a right click suspends, and neither selects.
    if (this.placement.active) {
      e.preventDefault()
      if (e.button === 0) {
        this.placement.onPointerMove(e.clientX, e.clientY)
        this.placement.commit()
      }
      else if (e.button === 2) this.placement.cancel()
      return
    }

    if (e.button === 0) {
      // Double-click on your own production building nominates it as the one
      // to build from. `e.detail` is the browser's own click counter, which is
      // already debounced and already matches whatever the platform considers
      // a double-click — reimplementing that with timers gets it wrong on
      // exactly the machines that configure it.
      if (e.detail >= 2 && this.orders.setPrimary(e.clientX, e.clientY)) return
      this.dragging = true
      this.camera.edgeScrollEnabled = false
      this.selection.beginDrag(e.clientX, e.clientY)
    } else if (e.button === 2) {
      e.preventDefault()
      // Z held turns each right-click into another leg of a route rather than a
      // new order, so a player can walk units around a cliff or away from a
      // defended approach instead of straight through it.
      this.orders.rightClick(e.clientX, e.clientY, this.waypointMode || e.shiftKey)
    }
  }

  private onPointerMove(e: PointerEvent): void {
    this.pointerX = e.clientX
    this.pointerY = e.clientY
    this.camera.onPointerMove(e.clientX, e.clientY)
    if (this.touchStart && Math.hypot(e.clientX - this.touchStart.x, e.clientY - this.touchStart.y) > 8) this.panning = true
    if (this.panning) {
      const scale = this.renderer.getLayout().scale
      this.renderer.panBy(Math.round((this.panX - e.clientX) / scale), Math.round((e.clientY - this.panY) / scale))
      this.panX = e.clientX
      this.panY = e.clientY
      this.root.style.cursor = 'grabbing'
      return
    }
    if (this.touchStart) return
    if (this.placement.active) {
      this.placement.onPointerMove(e.clientX, e.clientY)
      return
    }
    if (!this.dragging) return
    if (this.selection.updateDrag(e.clientX, e.clientY)) this.drawBox()
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.panning || this.touchStart) {
      if (this.touchStart && !this.panning) {
        const id = this.selection.pick(e.clientX, e.clientY)
        if (id !== 0) {
          if (this.selection.has(id)) this.selection.clear()
          else this.selection.set([id])
          this.syncHighlight()
        } else if (this.selection.count > 0) this.orders.rightClick(e.clientX, e.clientY)
      }
      this.endPan()
      return
    }
    if (e.button !== 0 || !this.dragging) return
    this.dragging = false
    this.camera.edgeScrollEnabled = true
    this.boxEl.style.display = 'none'

    const box = this.selection.endDrag()
    const additive = e.shiftKey

    if (box) {
      const picked = this.selection.pickBox(box.x0, box.y0, box.x1, box.y1)
      if (additive) this.selection.add(picked)
      else this.selection.set(picked)
      this.syncHighlight()
      return
    }

    const id = this.selection.pick(e.clientX, e.clientY)
    if (id === 0) {
      if (!additive) this.selection.clear()
    } else if (additive) {
      this.selection.toggle(id)
    } else if (e.detail >= 2) {
      const slot = this.entities.resolve(id)
      if (slot >= 0 && this.entities.has(slot, EF.Founder)) {
        // Double-clicking an MCV deploys it, which is where a player's hand
        // goes first and where Red Alert put it.
        this.selection.set([id])
        this.orders.issueDeploy()
      } else {
        this.selection.set(this.selection.pickSameKind(id))
      }
    } else {
      this.selection.set([id])
    }
    this.syncHighlight()
  }

  // --- keyboard -------------------------------------------------------------

  private endPan(): void {
    this.camera.onPointerLeave()
    this.panning = false
    this.touchStart = null
    this.root.style.cursor = ''
    this.camera.edgeScrollEnabled = true
  }

  /** True while Z is held: right-clicks append to a route. */
  private waypointMode = false

  private onKeyDown(e: KeyboardEvent): void {
    if (e.repeat) return
    // Somebody typing into the chat box is not issuing orders. Without this,
    // saying "stand down" scatters the army: every letter in it is a binding.
    if (isTyping(e.target)) return

    // Esc leaves a modal cursor first, and clears the selection otherwise.
    if (e.code === 'Escape') {
      if (this.placement.active) this.placement.cancel()
      else {
        this.selection.clear()
        this.syncHighlight()
      }
      return
    }
    // Every other key is inert while a modal cursor is up, apart from camera
    // movement — a player lining up a building still wants to pan.
    if (this.placement.active && !e.code.startsWith('Arrow')) return

    this.camera.onKeyDown(e.code)

    // Control groups first, because they claim the digits with a modifier held
    // and the tabs claim the same digits without one.
    //
    // Recall moved from a bare digit to Shift because 1-4 are the tabs now.
    // That is a real cost — recall is the most-pressed binding in the game —
    // but the alternative was cutting nine groups down to six, and a group you
    // cannot make at all is worse than one that takes a modifier to reach.
    const digit = digitFor(e.code)
    if (digit >= 0 && (e.ctrlKey || e.metaKey || e.shiftKey)) {
      if (e.ctrlKey || e.metaKey) {
        this.groups.assign(digit, this.selection.selected)
        this.ctx.emit('groups:assigned', { group: digit, count: this.selection.count })
      } else {
        this.selection.set(this.groups.recall(digit))
        this.syncHighlight()
      }
      return
    }

    if (e.code in TAB_KEYS) {
      this.hooks?.onTab(TAB_KEYS[e.code])
      return
    }

    switch (e.code) {
      // Where Red Alert 2 puts them, and where the game's own tutorial hints
      // say they are.
      case 'KeyA':
        this.orders.attackMove(this.pointerX, this.pointerY)
        break
      case 'KeyS':
        this.orders.issueStop()
        break
      case 'KeyD':
        this.orders.issueDeploy()
        break
      case 'KeyG':
        this.orders.issueGuard()
        break
      case 'KeyZ':
        this.waypointMode = true
        break
      // K and L are where Red Alert 2 puts repair and sell.
      case 'KeyK':
        this.orders.issueRepair()
        break
      case 'KeyL':
        this.orders.issueSell()
        break
      // Not from Red Alert, which has you click the flashing sidebar icon. A
      // finished structure waiting to be placed is easy to miss entirely, and
      // it was the first thing a new player got stuck on here.
      case 'KeyB':
        this.hooks?.onQuickPlace()
        break
      case 'KeyX':
        this.orders.issueScatter()
        break
      case 'KeyH':
        this.centerOnBase()
        break
      case 'KeyF':
        this.cycleIdleHarvester()
        break
      case 'KeyM':
        this.hooks?.onToggleMusic()
        break
      case 'F3':
        e.preventDefault()
        this.hooks?.onToggleNetPanel()
        break
      case 'Equal':
      case 'NumpadAdd':
        this.hooks?.onCycleSpeed()
        break
      case 'KeyC': {
        // Centre on the selection.
        const c = this.selection.centroid()
        if (c) this.hooks?.onCenter(c.x, c.z)
        break
      }
      case 'Space':
        // Centre on the last event. A distinct action from C, and both are
        // required — "where are my units" and "where did that happen" are
        // different questions.
        e.preventDefault()
        if (this.groups.lastEventX >= 0) {
          this.hooks?.onCenter(
            fix.toFloat(fix.raw(this.groups.lastEventX)),
            fix.toFloat(fix.raw(this.groups.lastEventY)),
          )
        }
        break
      default:
        break
    }
  }

  /**
   * Home. Centres on the base root, or on whatever the player has left.
   *
   * The fallback chain matters more than it looks: a player whose Command Yard
   * has just been destroyed is exactly the player who most needs to find the
   * rest of their base, and a key that silently does nothing at that moment
   * reads as the game ignoring them.
   */
  private centerOnBase(): void {
    const target = this.baseAnchor()
    if (target === null) {
      this.ctx.emit('order:rejected', { owner: this.localSlot, reason: 'stale-entity', detail: 'nothing left to centre on' })
      return
    }
    this.hooks?.onCenter(target[0], target[1])
  }

  /** Base root first, then any structure, then any unit. */
  private baseAnchor(): [number, number] | null {
    let structure: [number, number] | null = null
    let anything: [number, number] | null = null
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (this.entities.owner[slot] !== this.localSlot) continue
      const at: [number, number] = [
        fix.toFloat(fix.raw(this.entities.x[slot])),
        fix.toFloat(fix.raw(this.entities.y[slot])),
      ]
      const def = defById(this.entities.kind[slot])
      if (def?.role === ROLE.BaseRoot) return at
      if (!structure && this.entities.has(slot, EF.Structure)) structure = at
      if (!anything) anything = at
    }
    return structure ?? anything
  }

  private cycleIdleHarvester(): void {
    const next = this.groups.nextIdleHarvester(this.localSlot, this.lastCycledHarvester)
    if (next === 0) {
      this.ctx.emit('order:rejected', { owner: this.localSlot, reason: 'no-producer', detail: 'no idle harvester' })
      return
    }
    this.lastCycledHarvester = next
    this.selection.set([next])
    this.syncHighlight()
    const slot = this.entities.resolve(next)
    if (slot >= 0) {
      this.hooks?.onCenter(
        fix.toFloat(fix.raw(this.entities.x[slot])),
        fix.toFloat(fix.raw(this.entities.y[slot])),
      )
    }
  }

  // --- helpers --------------------------------------------------------------

  private drawBox(): void {
    const d = this.selection.drag
    this.boxEl.style.display = 'block'
    this.boxEl.style.left = `${Math.min(d.x0, d.x1)}px`
    this.boxEl.style.top = `${Math.min(d.y0, d.y1)}px`
    this.boxEl.style.width = `${Math.abs(d.x1 - d.x0)}px`
    this.boxEl.style.height = `${Math.abs(d.y1 - d.y0)}px`
  }

  private syncHighlight(): void {
    this.renderer.setHighlight(this.selection.selected)
    this.ctx.emit('selection:changed', { ids: this.selection.selected })
  }

  dispose(): void {
    for (const d of this.disposers) d()
    this.disposers.length = 0
    this.boxEl.remove()
  }
}

/** Digit for `Digit0`–`Digit9`, or -1. */
function digitFor(code: string): number {
  if (!code.startsWith('Digit') || code.length !== 6) return -1
  const n = code.charCodeAt(5) - 48
  return n >= 0 && n <= 9 ? n : -1
}

/**
 * Whether a key event is somebody typing into a field rather than playing.
 *
 * The handlers are on `window` so that keys work wherever the pointer is, which
 * also means they see every keystroke aimed at the chat box.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable
}
